import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Option,
  OptionKey,
  RequiredIcon,
  Text,
  ToggleButtonGroup,
  useResponsive,
} from '@common-fe/common-fe';
import styled, { css, keyframes } from 'styled-components';

import useFileErrorPopup from '@/components/controls/DropScan/hooks/useFileErrorPopup';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import { DocumentMayBeRequiredWarning, UseMyMoneyDropScan } from '@/modules/UseMyMoney/components';
import { useMyMoneyNavigation } from '@/modules/UseMyMoney/hooks';
import { useDocumentOcrStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { ReceiptUploadOption, UseMyMoneyStep } from '@/modules/UseMyMoney/UseMyMoney.types';

const fadeIn = keyframes`
  from {
    opacity: 0.6;
  }
  to {
    opacity: 1;
  }
`;

const AnimatedBox = styled(Box)`
  ${() => css`
    animation: ${fadeIn} 0.2s ease-in-out forwards;
  `}
`;

const OPTIONS: Option[] = [
  {
    key: ReceiptUploadOption.YES_I_HAVE_ONE,
    value: ReceiptUploadOption.YES_I_HAVE_ONE,
    title: 'Yes, I have one',
  },
  {
    key: ReceiptUploadOption.NO_SKIP_UPLOAD,
    value: ReceiptUploadOption.NO_SKIP_UPLOAD,
    title: 'No, skip upload',
  },
];

interface Props {
  storeKey: string;
  onUpload: () => void;
  deleteDocument: (documentId: string) => void;
  children: React.ReactNode;
}

const SelectFile: React.FC<Props> = ({ storeKey, onUpload, deleteDocument, children }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { receiptUploadOption, setReceiptUploadOption } = useMyMoneyStore();
  const { handleSetStep } = useMyMoneyNavigation();
  const { resetDocumentOcrState } = useDocumentOcrStore();

  const handleUploadDocument = useCallback(() => {
    setReceiptUploadOption(ReceiptUploadOption.YES_I_HAVE_ONE);
  }, [setReceiptUploadOption]);

  const handleSkipUploadDocument = useCallback(() => {
    resetDocumentOcrState();
    handleSetStep(UseMyMoneyStep.SUMMARY);
  }, [handleSetStep, resetDocumentOcrState]);

  const {
    fileUploadError,
    isFileUploadErrorModalVisible,
    onCloseFileUploadError,
    onFileUploadError,
  } = useFileErrorPopup();

  return (
    <>
      <Box data-testid="select-file_container">
        <Box direction="row" align="center" height={{ min: '24px' }}>
          <Text data-testid="use-my-money-options-list_question" weight={700}>
            <RequiredIcon size="10px" color="danger" /> {t('Do you have a receipt, invoice, or EOB?')}
          </Text>
        </Box>

        <Box margin={{ top: 'spacing8' }}>
          <ToggleButtonGroup
            testId="select-file"
            columns={['1fr', '1fr']}
            gap="spacing12"
            options={OPTIONS}
            value={receiptUploadOption}
            onChange={(value: OptionKey) => setReceiptUploadOption(value as ReceiptUploadOption)}
          />
        </Box>

        <UseMyMoneyDropScan
          storeKey={storeKey}
          onUpload={onUpload}
          onFileUploadError={onFileUploadError}
          deleteDocument={deleteDocument}
        />

        {receiptUploadOption === ReceiptUploadOption.YES_I_HAVE_ONE && (
          <AnimatedBox margin={{ top: 'spacing24' }}>{children}</AnimatedBox>
        )}

        {receiptUploadOption === ReceiptUploadOption.NO_SKIP_UPLOAD && (
          <AnimatedBox margin={{ top: 'spacing24' }}>
            <DocumentMayBeRequiredWarning
              isMobile={isMobile}
              onUploadDocument={handleUploadDocument}
              onSkipUploadDocument={handleSkipUploadDocument}
            />
          </AnimatedBox>
        )}
      </Box>

      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup
          fileError={fileUploadError}
          onClose={onCloseFileUploadError}
        />
      )}
    </>
  );
};

export default SelectFile;
